import type { PaginatedResult } from '@api/resources/resource';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ResourceWithAttachments } from './attachments';
import { Conversation, Dispute, DisputeItem, Email, EmailThread, Call } from '../models';
import { QueryParams } from '../query';
import { DEFAULT_TABLE_PAGE_SIZE } from '@stuut/common/src/constants';

export interface DisputeCurrencyTotal {
  amount: number;
  invoice_count: number;
}

export interface DisputeOverview {
  open_cases: number;
  ready_to_resolve: number;
  open_dispute_task_counts: {
    dispute_create: number;
    dispute_activate: number;
  };
  status_breakdown: Partial<
    Record<'proposed' | 'in_process' | 'needs_review' | 'resolved' | 'closed', number>
  >;
  in_dispute_by_currency: Record<string, DisputeCurrencyTotal>;
  in_dispute_overdue_by_currency: Record<string, DisputeCurrencyTotal>;
  currency: string;
  total_in_dispute_amount: number;
  total_in_dispute_invoice_count: number;
  total_in_dispute_overdue_amount: number;
  total_in_dispute_overdue_invoice_count: number;
}

export class Disputes extends ResourceWithAttachments<Dispute> {
  model = Dispute;

  resourcePath = 'v1/disputes';

  async activate(id, dispute?: Dispute) {
    const response = await this.api.post(this.path(id, 'activate'));

    if (dispute?.id) dispute?.updateFromJson(response?.data);

    return response?.data;
  }

  async updateItem(id, args, dispute: Dispute) {
    const itemId = args.itemId || dispute?.disputeItemId;

    const response = await this.api.post(this.path(id, 'items', itemId), {
      data: args.data,
    });

    if (dispute?.id && dispute?.disputeItem?.id) {
      dispute?.disputeItem?.updateFromJson(response?.data);
      dispute.items = [dispute?.disputeItem];
    }

    return response?.data;
  }

  async resolve(id: string, dispute: Dispute) {
    const response = await this.api.post(this.path(id, 'resolve'));

    if (dispute?.id) dispute?.updateFromJson(response?.data);

    return response?.data;
  }

  async communications(id: string, args?: AxiosRequestConfig, dispute?: Dispute) {
    let params = args?.params;

    if (params && !(params instanceof QueryParams)) {
      params = {
        page: 0,
        limit: DEFAULT_TABLE_PAGE_SIZE,
        ...params,
      };
    }

    if (params instanceof QueryParams) {
      params = params.serialize({ forApiRequest: true });
    }

    const response = await this.api.get(this.path(id, 'communications'), { params });
    const isPlainArray = Array.isArray(response.data);
    const rawData = isPlainArray ? response.data : response.data.data || [];
    const serializedData = rawData.map((json: any) => Conversation.fromJson(json));

    return { ...response.data, data: serializedData } as PaginatedResult<Conversation>;
  }

  async share(id: string, data: { to_emails: string[] }) {
    const response = await this.api.post(this.path(id, 'share'), { data });
    return response?.data;
  }

  async retrieveOverview(assignee?: string): Promise<DisputeOverview> {
    const response = await this.api.get(this.path('overview'), { params: { assignee } });

    return response.data;
  }
}
